import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import { t } from 'ember-intl';
import PowerSelect from 'ember-power-select/components/power-select';
import { eq, gt, lt } from 'ember-truth-helpers';

import Button from 'umg-ember-backend/components/ui/button/index';
import { getBem } from 'umg-ember-backend/utils/get-bem';

import styles from './styles.module.css';

const bem = getBem(styles);

interface PaginationSignature {
  Args: {
    pagination: {
      total: number;
      offset: number;
      limit: number;
      pages: number;
    };
    setPage: (page: number) => void;
    setPageLimit: (limit: number) => void;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

export default class UiPagination extends Component<PaginationSignature> {
  // Services
  @service declare media: TODO;

  // Defaults
  itemsPerPage = [5, 15, 30, 60, 90];

  // Getter and setter
  get defaultVisiblePages() {
    return this.media.isMobile ? 1 : 5;
  }

  get pagination() {
    return this.args.pagination ?? { offset: 0, pages: 0 };
  }

  get currentPage() {
    return this.pagination.offset / this.pagination.limit + 1;
  }

  get firstPage() {
    return 1;
  }

  get hasPages() {
    return this.pagination.pages > 1;
  }

  get lastPage() {
    return this.pagination.pages;
  }

  get nextPage() {
    return this.currentPage + 1;
  }

  get prevPage() {
    return this.currentPage - 1;
  }

  get rangeStart() {
    const rangeStart = this.currentPage - Math.floor(this.visiblePages / 2);

    if (rangeStart < 1) {
      return 1;
    }

    if (rangeStart + this.visiblePages - 1 > this.lastPage) {
      return this.lastPage - this.visiblePages + 1;
    }

    return rangeStart;
  }

  get rangeEnd() {
    return this.rangeStart + this.visiblePages - 1;
  }

  get showFirstPage() {
    return this.rangeStart > 1 && this.lastPage > 8;
  }

  get showStartMore() {
    return this.rangeStart > 2 && this.lastPage > 8;
  }

  get showLastPage() {
    return this.rangeEnd < this.lastPage && this.lastPage > 8;
  }

  get showEndMore() {
    return this.rangeEnd < this.lastPage - 1 && this.lastPage > 8;
  }

  get visiblePages() {
    if (this.lastPage < this.defaultVisiblePages) {
      return this.lastPage;
    }

    return this.defaultVisiblePages;
  }

  get pages() {
    let start = this.rangeStart;
    let visiblePages = this.visiblePages;

    if (this.lastPage > this.defaultVisiblePages) {
      if (!this.showFirstPage && visiblePages + 1 < this.lastPage) {
        visiblePages++;
      }

      if (!this.showStartMore && visiblePages + 1 < this.lastPage) {
        visiblePages++;
      }

      if (!this.showLastPage && start - 1 > this.firstPage) {
        visiblePages++;
        start--;
      }

      if (!this.showEndMore && start - 1 > this.firstPage) {
        visiblePages++;
        start--;
      }
    }

    if (this.lastPage < 9) {
      visiblePages = this.lastPage;
      start = 1;
    }

    // VERSION 2
    // const extraPages = [!this.showFirstPage, !this.showStartMore, !this.showLastPage, !this.showEndMore].filter(Boolean).length

    // if (visiblePages + extraPages < this.lastPage - 1) {
    //   visiblePages = visiblePages + extraPages;
    // } else {
    //   visiblePages = this.lastPage;
    // }

    // const offsetStart = [!this.showLastPage, !this.showEndMore].filter(Boolean).length

    // if (start - offsetStart > this.firstPage) {
    //   start = start - offsetStart
    // }

    return Array.from({ length: visiblePages }, (_, i) => start + i);
  }

  // Template
  <template>
    {{#if this.pagination}}
      <div class={{(bem)}} ...attributes>
        <div class={{bem 'pages'}}>
          {{#if this.hasPages}}
            <Button
              @disabled={{lt this.prevPage 1}}
              @icon='previous'
              @style='outline'
              {{on 'click' (fn @setPage this.prevPage)}}
            />

            {{#if this.showFirstPage}}
              <Button
                @label={{this.firstPage}}
                @style='outline'
                data-first
                {{on 'click' (fn @setPage this.firstPage)}}
              />
            {{/if}}

            {{#if this.showStartMore}}
              <div class={{bem 'more'}}>
                {{t 'ui.pagination.more_indicator'}}
              </div>
            {{/if}}

            {{#each this.pages as |page|}}
              <Button
                @label={{page}}
                @style={{if (eq this.currentPage page) 'active' 'outline'}}
                data-page={{page}}
                {{on 'click' (fn @setPage page)}}
              />
            {{/each}}

            {{#if this.showEndMore}}
              <div class={{bem 'more'}}>
                {{t 'ui.pagination.more_indicator'}}
              </div>
            {{/if}}

            {{#if this.showLastPage}}
              <Button
                @label={{this.lastPage}}
                @style='outline'
                data-last
                {{on 'click' (fn @setPage this.lastPage)}}
              />
            {{/if}}

            <Button
              @disabled={{gt this.nextPage this.lastPage}}
              @icon='next'
              @style='outline'
              {{on 'click' (fn @setPage this.nextPage)}}
            />
          {{/if}}
        </div>

        <div class={{bem 'limit'}}>
          <label class={{bem 'label'}}>
            {{t 'ui.pagination.per_page'}}
          </label>
          <PowerSelect
            @options={{this.itemsPerPage}}
            @selected={{this.pagination.limit}}
            @searchEnabled={{false}}
            @onChange={{@setPageLimit}}
            as |name|
          >
            {{name}}
          </PowerSelect>
        </div>
      </div>
    {{/if}}
  </template>
}
